import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import api from "../../services/api";
import Template from "../../template";
import * as S from "./styled";

/*
  Tela da coordenação para conferir os contratos dos responsáveis e liberar o aluno.

  Backend:
    GET  /get-status-contratos  — lista com filtros e paginação
    POST /libera-aluno          — aprova (libera o aluno) ou rejeita (permite reenvio)

  Só o contrato em ENVIADO pode ser avaliado: PENDENTE ainda não foi enviado,
  APROVADO/REJEITADO já foram decididos.
*/

const STATUS = ["PENDENTE", "ENVIADO", "APROVADO", "REJEITADO"] as const;

type Status = (typeof STATUS)[number];

interface Contrato {
  id: number;
  status_contrato: Status;
  url_contrato: string | null;
  /* Contrato em branco gerado no cadastro — serve para reenviar ao responsável. */
  url_contrato_gerado: string | null;
  motivo_rejeicao: string | null;
  url_assinatura: string | null;
  created_at: string;
  aluno: { id: number; nome: string; email: string; cpf: string; turma: string; ativado: boolean };
  responsavel: { id: number; nome: string; cpf: string; email: string; telefone: string | null };
}

const FILTROS_VAZIOS = { status: "todos", aluno: "", responsavel: "", cpf: "", turma: "" };

const POR_PAGINA = 10;

function formatarCpf(cpf: string) {
  const d = String(cpf ?? "").replace(/\D/g, "");
  return d.length === 11 ? d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4") : cpf || "—";
}

function formatarData(valor: string) {
  const d = new Date(valor);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("pt-BR");
}

function Contratos() {
  const [contratos, setContratos] = useState<Contrato[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [total, setTotal] = useState(0);
  const [filtros, setFiltros] = useState(FILTROS_VAZIOS);

  /* Contadores por status — vêm dentro da resposta da própria listagem. */
  const [contagem, setContagem] = useState<Record<string, number>>({});

  const [confirmando, setConfirmando] = useState<{ contrato: Contrato; aprovado: boolean } | null>(null);
  const [salvando, setSalvando] = useState(false);

  /* Justificativa da recusa: obrigatória, vai no email para o responsável. */
  const [motivo, setMotivo] = useState("");
  const [erroMotivo, setErroMotivo] = useState("");

  /*
    Linha com o menu de ações aberto (só um por vez). A direção é decidida na
    abertura, medindo o espaço abaixo do botão — assim o menu não fica cortado
    no rodapé nem cobre o cabeçalho quando há sobra de espaço.
  */
  const [menuAberto, setMenuAberto] = useState<{ id: number; paraCima: boolean } | null>(null);
  const ALTURA_MENU = 200;
  const areaTabela = useRef<HTMLDivElement>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);

    try {
      const params: Record<string, string> = {
        page: String(pagina),
        limit: String(POR_PAGINA),
        status: filtros.status,
      };

      // Só manda o que foi preenchido: campo vazio viraria filtro por string vazia.
      (["aluno", "responsavel", "cpf", "turma"] as const).forEach((campo) => {
        const valor = filtros[campo].trim();
        if (valor) params[campo] = valor;
      });

      const { data } = await api.get("/get-status-contratos", { params });

      setContratos(data.data || []);
      setTotalPaginas(data.paginacao?.totalPages || 1);
      setTotal(data.paginacao?.totalCount || 0);
      setContagem(data.contagem || {});
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Não foi possível carregar os contratos."
      );
      setContratos([]);
    } finally {
      setCarregando(false);
    }
  }, [pagina, filtros]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  /* Menu suspenso precisa fechar ao clicar fora e no Esc. */
  useEffect(() => {
    if (menuAberto === null) return;

    const aoClicarFora = (e: MouseEvent) => {
      if (!areaTabela.current?.contains(e.target as Node)) {
        setMenuAberto(null);
      }
    };
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuAberto(null);
    };

    document.addEventListener("mousedown", aoClicarFora);
    document.addEventListener("keydown", aoTeclar);

    return () => {
      document.removeEventListener("mousedown", aoClicarFora);
      document.removeEventListener("keydown", aoTeclar);
    };
  }, [menuAberto]);

  /* Rolar com o menu aberto o deixaria descolado da linha. */
  useEffect(() => {
    if (menuAberto === null) return;

    const fechar = () => setMenuAberto(null);
    window.addEventListener("scroll", fechar, true);

    return () => window.removeEventListener("scroll", fechar, true);
  }, [menuAberto]);

  function alterarFiltro(campo: string, valor: string) {
    setMenuAberto(null);
    setPagina(1);
    setFiltros((atual) => ({ ...atual, [campo]: valor }));
  }

  function abrirConfirmacao(contrato: Contrato, aprovado: boolean) {
    setMotivo("");
    setErroMotivo("");
    setConfirmando({ contrato, aprovado });
  }

  function fecharConfirmacao() {
    setConfirmando(null);
    setMotivo("");
    setErroMotivo("");
  }

  async function confirmar() {
    if (!confirmando) return;

    const justificativa = motivo.trim();

    // Mesma regra do backend: recusar sem explicar deixa o responsável sem saída.
    if (!confirmando.aprovado && justificativa.length < 5) {
      setErroMotivo("Descreva o que precisa ser ajustado (mínimo de 5 caracteres).");
      return;
    }

    setSalvando(true);

    try {
      const { data } = await api.post("/libera-aluno", {
        id_relacao: confirmando.contrato.id,
        aprovado: confirmando.aprovado,
        ...(confirmando.aprovado ? {} : { motivo: justificativa }),
      });

      toast.success(data.message);
      fecharConfirmacao();
      await carregar();
    } catch (error: any) {
      const status = error?.response?.status;
      const mensagem = error?.response?.data?.message;

      /*
        409/422 significam que o contrato saiu do estado esperado (outra pessoa avaliou,
        ou o responsável reenviou). Recarrega para a tela refletir o estado real.
      */
      toast.error(mensagem || "Não foi possível registrar a decisão.");

      if (status === 409 || status === 422 || status === 404) {
        fecharConfirmacao();
        await carregar();
      }
    } finally {
      setSalvando(false);
    }
  }

  async function copiarLink(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link de assinatura copiado.");
    } catch {
      toast.error("Não foi possível copiar. Copie manualmente: " + url);
    }
  }

  return (
    <Template>
      <S.Pagina>
        <S.Cabecalho>
          <S.Identidade>
            <S.IconeTitulo>
              <i className="fas fa-file-signature" />
            </S.IconeTitulo>
            <div>
              <h1>Contratos dos responsáveis</h1>
              <p>
                Confira o contrato assinado e libere o acesso do aluno. Só contratos
                com status <strong>ENVIADO</strong> podem ser avaliados.
              </p>
            </div>
          </S.Identidade>

          <S.Contadores>
            {STATUS.map((status) => {
              const cor = S.CORES_STATUS[status];

              return (
                <S.Contador
                  key={status}
                  $ativo={filtros.status === status}
                  $cor={cor.ponto}
                  $fundo={cor.fundo}
                  $borda={cor.borda}
                  onClick={() =>
                    alterarFiltro("status", filtros.status === status ? "todos" : status)
                  }
                  title={`Filtrar por ${status}`}
                >
                  <strong>{contagem[status] ?? "—"}</strong>
                  <span>{status}</span>
                </S.Contador>
              );
            })}
          </S.Contadores>
        </S.Cabecalho>

        <S.Filtros>
          <S.Campo>
            <label>Status</label>
            <div className="entrada">
              <i className="fas fa-filter" />
              <select
                value={filtros.status}
                onChange={(e) => alterarFiltro("status", e.target.value)}
              >
                <option value="todos">Todos</option>
                {STATUS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </S.Campo>

          <S.Campo>
            <label>Aluno</label>
            <div className="entrada">
              <i className="fas fa-user-graduate" />
              <input
                placeholder="Nome do aluno"
                value={filtros.aluno}
                onChange={(e) => alterarFiltro("aluno", e.target.value)}
              />
            </div>
          </S.Campo>

          <S.Campo>
            <label>Responsável</label>
            <div className="entrada">
              <i className="fas fa-user-shield" />
              <input
                placeholder="Nome do responsável"
                value={filtros.responsavel}
                onChange={(e) => alterarFiltro("responsavel", e.target.value)}
              />
            </div>
          </S.Campo>

          <S.Campo>
            <label>CPF</label>
            <div className="entrada">
              <i className="fas fa-id-card" />
              <input
                placeholder="Do aluno ou do responsável"
                value={filtros.cpf}
                onChange={(e) => alterarFiltro("cpf", e.target.value)}
              />
            </div>
          </S.Campo>

          <S.Campo>
            <label>Turma</label>
            <div className="entrada">
              <i className="fas fa-chalkboard" />
              <input
                placeholder="Nome da turma"
                value={filtros.turma}
                onChange={(e) => alterarFiltro("turma", e.target.value)}
              />
            </div>
          </S.Campo>

          <S.BotaoLimpar
            onClick={() => {
              setPagina(1);
              setFiltros(FILTROS_VAZIOS);
            }}
          >
            <i className="fas fa-eraser" /> Limpar filtros
          </S.BotaoLimpar>
        </S.Filtros>

        <S.Tabela ref={areaTabela}>
          <S.Titulos>
            <span>Aluno</span>
            <span>Responsável</span>
            <span>Turma / Envio</span>
            <span>Status</span>
            <span style={{ textAlign: "right" }}>Ações</span>
          </S.Titulos>

          {carregando ? (
            <S.Carregando>
              <div className="roda" />
              Carregando contratos...
            </S.Carregando>
          ) : contratos.length === 0 ? (
            <S.Vazio>
              <i className="fas fa-folder-open" />
              <strong>Nenhum contrato encontrado</strong>
              <span>Ajuste os filtros ou aguarde o cadastro de novos alunos.</span>
            </S.Vazio>
          ) : (
            contratos.map((contrato) => {
              const podeAvaliar = contrato.status_contrato === "ENVIADO";

              return (
                <S.Linha key={contrato.id}>
                  <S.Celula>
                    <span className="principal">{contrato.aluno.nome}</span>
                    <span className="apoio">{formatarCpf(contrato.aluno.cpf)}</span>
                    <S.Acesso $liberado={contrato.aluno.ativado}>
                      <i
                        className={
                          contrato.aluno.ativado ? "fas fa-unlock" : "fas fa-lock"
                        }
                      />
                      {contrato.aluno.ativado ? "Acesso liberado" : "Acesso bloqueado"}
                    </S.Acesso>
                  </S.Celula>

                  <S.Celula>
                    <span className="principal">{contrato.responsavel.nome}</span>
                    <span className="apoio">{contrato.responsavel.email}</span>
                    <span className="apoio">{formatarCpf(contrato.responsavel.cpf)}</span>
                  </S.Celula>

                  <S.Celula>
                    <span className="principal">{contrato.aluno.turma}</span>
                    <span className="apoio">
                      Cadastro em {formatarData(contrato.created_at)}
                    </span>
                  </S.Celula>

                  <div>
                    <S.Badge $status={contrato.status_contrato}>
                      {contrato.status_contrato}
                    </S.Badge>
                    {contrato.status_contrato === "REJEITADO" && contrato.motivo_rejeicao && (
                      <S.MotivoLinha title={contrato.motivo_rejeicao}>
                        {contrato.motivo_rejeicao}
                      </S.MotivoLinha>
                    )}
                  </div>

                  <S.Acoes>
                    <S.BotaoMenu
                      type="button"
                      $aberto={menuAberto?.id === contrato.id}
                      onClick={(e) => {
                        if (menuAberto?.id === contrato.id) {
                          setMenuAberto(null);
                          return;
                        }

                        const base = e.currentTarget.getBoundingClientRect();
                        const espacoAbaixo = window.innerHeight - base.bottom;

                        setMenuAberto({
                          id: contrato.id,
                          paraCima: espacoAbaixo < ALTURA_MENU && base.top > ALTURA_MENU,
                        });
                      }}
                      aria-haspopup="menu"
                      aria-expanded={menuAberto?.id === contrato.id}
                      aria-label={`Ações do contrato de ${contrato.aluno.nome}`}
                      title="Ações"
                    >
                      <i className="fas fa-ellipsis-vertical" />
                    </S.BotaoMenu>

                    {menuAberto?.id === contrato.id && (
                      <S.Menu role="menu" $paraCima={menuAberto.paraCima}>
                        {contrato.url_contrato && (
                          <S.MenuItem
                            as="a"
                            role="menuitem"
                            href={contrato.url_contrato}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setMenuAberto(null)}
                          >
                            <i className="fas fa-file-pdf" /> Ver contrato assinado
                          </S.MenuItem>
                        )}

                        {/* Cópia do contrato em branco, para reenviar caso o anexo do email falhe. */}
                        {contrato.url_contrato_gerado && (
                          <S.MenuItem
                            as="a"
                            role="menuitem"
                            href={contrato.url_contrato_gerado}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setMenuAberto(null)}
                          >
                            <i className="fas fa-file-arrow-down" /> Baixar contrato não assinado
                          </S.MenuItem>
                        )}

                        {contrato.url_assinatura && (
                          <S.MenuItem
                            type="button"
                            role="menuitem"
                            onClick={() => {
                              copiarLink(contrato.url_assinatura as string);
                              setMenuAberto(null);
                            }}
                          >
                            <i className="fas fa-link" /> Copiar link de assinatura
                          </S.MenuItem>
                        )}

                        {podeAvaliar && (
                          <>
                            <S.MenuDivisor />
                            <S.MenuItem
                              type="button"
                              role="menuitem"
                              $tom="aprovar"
                              onClick={() => {
                                abrirConfirmacao(contrato, true);
                                setMenuAberto(null);
                              }}
                            >
                              <i className="fas fa-check" /> Aprovar e liberar aluno
                            </S.MenuItem>
                            <S.MenuItem
                              type="button"
                              role="menuitem"
                              $tom="rejeitar"
                              onClick={() => {
                                abrirConfirmacao(contrato, false);
                                setMenuAberto(null);
                              }}
                            >
                              <i className="fas fa-xmark" /> Rejeitar contrato
                            </S.MenuItem>
                          </>
                        )}
                      </S.Menu>
                    )}
                  </S.Acoes>
                </S.Linha>
              );
            })
          )}
        </S.Tabela>

        <S.Paginacao>
          <span className="resumo">
            <strong>{total}</strong> contrato{total === 1 ? "" : "s"} · página{" "}
            <strong>{pagina}</strong> de <strong>{totalPaginas}</strong>
          </span>
          <div className="navegacao">
            <button disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)}>
              <i className="fas fa-chevron-left" /> Anterior
            </button>
            <button
              disabled={pagina >= totalPaginas}
              onClick={() => setPagina((p) => p + 1)}
            >
              Próxima <i className="fas fa-chevron-right" />
            </button>
          </div>
        </S.Paginacao>
      </S.Pagina>

      {confirmando && (
        <S.Overlay onClick={() => !salvando && fecharConfirmacao()}>
          <S.Dialogo onClick={(e) => e.stopPropagation()}>
            <S.DialogoTopo $aprovar={confirmando.aprovado}>
              <i
                className={
                  confirmando.aprovado
                    ? "fas fa-circle-check"
                    : "fas fa-circle-exclamation"
                }
              />
              <h2>
                {confirmando.aprovado ? "Aprovar contrato?" : "Rejeitar contrato?"}
              </h2>
            </S.DialogoTopo>

            <S.DialogoCorpo>
              <div className="resumo">
                <div>
                  Aluno: <strong>{confirmando.contrato.aluno.nome}</strong>
                </div>
                <div>
                  Responsável: <strong>{confirmando.contrato.responsavel.nome}</strong>
                </div>
              </div>
              <p>
                {confirmando.aprovado
                  ? "O acesso do aluno à plataforma será liberado e o link de assinatura deixará de funcionar."
                  : "O responsável receberá este motivo por e-mail e poderá reenviar o contrato pelo mesmo link."}
              </p>

              {!confirmando.aprovado && (
                <S.CampoMotivo $erro={!!erroMotivo}>
                  <label htmlFor="motivo-recusa">O que precisa ser ajustado</label>
                  <textarea
                    id="motivo-recusa"
                    rows={3}
                    maxLength={1000}
                    autoFocus
                    disabled={salvando}
                    placeholder="Ex.: a última página está sem a assinatura do responsável."
                    value={motivo}
                    onChange={(e) => {
                      setMotivo(e.target.value);
                      if (erroMotivo) setErroMotivo("");
                    }}
                  />
                  <div className="rodape">
                    <span className="erro">{erroMotivo}</span>
                    <span className="contador">{motivo.length}/1000</span>
                  </div>
                </S.CampoMotivo>
              )}

              <S.DialogoAcoes>
                <S.BotaoLink
                  type="button"
                  disabled={salvando}
                  onClick={fecharConfirmacao}
                >
                  Cancelar
                </S.BotaoLink>
                {confirmando.aprovado ? (
                  <S.BotaoAprovar type="button" disabled={salvando} onClick={confirmar}>
                    <i className="fas fa-check" />
                    {salvando ? "Aprovando..." : "Aprovar e liberar aluno"}
                  </S.BotaoAprovar>
                ) : (
                  <S.BotaoRejeitar type="button" disabled={salvando} onClick={confirmar}>
                    <i className="fas fa-xmark" />
                    {salvando ? "Rejeitando..." : "Rejeitar contrato"}
                  </S.BotaoRejeitar>
                )}
              </S.DialogoAcoes>
            </S.DialogoCorpo>
          </S.Dialogo>
        </S.Overlay>
      )}
    </Template>
  );
}

export default Contratos;
